import { useEffect, useState } from 'react';
import type { Icon as PhosphorIconComponent } from '@phosphor-icons/react';

// @phosphor-icons/react's namespace export (`import * as PhosphorIcons`) pulls in all
// ~1500 icons (every weight) because dynamic property access (`PhosphorIcons[name]`)
// defeats bundler tree-shaking — every call site that did this shipped the whole icon
// library in its own chunk. A dynamic import() keeps it out of the eager bundle; it's
// fetched once, lazily, on first icon render, and cached for the rest of the session.
type PhosphorModule = Record<string, PhosphorIconComponent>;

let modulePromise: Promise<PhosphorModule> | null = null;
let cachedModule: PhosphorModule | null = null;

function loadPhosphorModule(): Promise<PhosphorModule> {
  if (!modulePromise) {
    modulePromise = import('@phosphor-icons/react').then((mod) => {
      cachedModule = mod as unknown as PhosphorModule;
      return cachedModule;
    });
  }
  return modulePromise;
}

/**
 * Kicks off the lazy @phosphor-icons/react fetch immediately instead of waiting for
 * the first phosphor-typed icon to mount and trigger it from useEffect. Call once,
 * as early as possible in the boot sequence (see index.tsx) — every render before
 * this resolves shows a blank glyph (see AppIcon.tsx's `PhosphorIconComponent ===
 * undefined` branch), so giving it a head start during auth/router bootstrap makes
 * it very unlikely any real icon ever renders before the module is ready.
 * Safe to call multiple times; the underlying fetch is deduped by loadPhosphorModule.
 */
export function preloadPhosphorIcons(): void {
  loadPhosphorModule().catch(() => {});
}

/**
 * Resolves a Phosphor icon component by its string name (e.g. "PencilSimple"), lazily
 * loading @phosphor-icons/react on first use instead of bundling it eagerly.
 *
 * Returns:
 * - `undefined` while the icon module is still loading (brief, one-time per session)
 * - `null` once loaded, if no icon name was given or the name doesn't exist
 * - the resolved component otherwise
 */
export function usePhosphorIcon(iconName: string | null | undefined): PhosphorIconComponent | null | undefined {
  const [module, setModule] = useState<PhosphorModule | null>(cachedModule);

  useEffect(() => {
    if (!iconName || cachedModule) return;
    let cancelled = false;
    loadPhosphorModule().then((mod) => {
      if (!cancelled) setModule(mod);
    });
    return () => {
      cancelled = true;
    };
  }, [iconName]);

  if (!iconName) return null;
  if (!module) return undefined;
  return module[iconName] ?? null;
}

export default usePhosphorIcon;
